import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type { ItemBehaviorController } from '@toolproof-core/visualization/_contract/item';

import { deriveProjectionRuntimeState } from '@/workflow-builder/_pure/derivation/projectionRuntimeState';
import { deriveItemIntentRoute } from '@/workflow-builder/_pure/interaction/itemIntentRouting';
import { isRuntimeState } from '@/workflow-builder/_pure/interaction/modeAugmentationHelpers';
import { createActionContext } from '@/workflow-builder/_pure/model/context';
import { dispatchAction } from '@/workflow-builder/model/commands';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { RuntimeCallbacks } from '@/workflow/_contract/runtime';

export function deriveItemBehavior(args: {
    runtimeState: unknown;
    cosmosData: CosmosDataModel;
    config: Config;
    baseItemBehavior: ItemBehaviorController | undefined;
    runtimeCallbacks?: RuntimeCallbacks;
    allowRenderedToolStepClicksForRebinding?: boolean;
}): ItemBehaviorController | undefined {
    const runtimeState = args.runtimeState;
    if (!isRuntimeState(runtimeState)) {
        return args.baseItemBehavior;
    }

    const projectionRuntimeState = deriveProjectionRuntimeState(runtimeState);
    const actionContext = createActionContext({
        config: args.config,
        cosmosData: args.cosmosData,
        runtimeState,
    });
    const runtimeCallbacks = args.runtimeCallbacks ?? args.config.runtimeCallbacks;

    return {
        ...(args.baseItemBehavior ?? {}),
        onIntent: (intent) => {
            const route = deriveItemIntentRoute({
                runtimeState,
                projectionRuntimeState,
                intent,
                allowRenderedToolStepClicksForRebinding: args.allowRenderedToolStepClicksForRebinding ?? false,
                selectableToolHandleSet: actionContext.selectableToolHandleSet,
            });

            switch (route.kind) {
                case 'tool-selection': {
                    const nextBuilderRuntimeState = dispatchAction({
                        config: args.config,
                        runtimeState,
                        cosmosData: args.cosmosData,
                        action: {
                            command: 'create_tool_step',
                            toolHandle: route.toolHandle as ToolHandle,
                        },
                    });

                    if (nextBuilderRuntimeState !== runtimeState) {
                        runtimeCallbacks?.onRuntimeStateChange?.(nextBuilderRuntimeState);
                    }

                    args.baseItemBehavior?.onIntent?.(intent);
                    return;
                }
                case 'select-input': {
                    const nextBuilderRuntimeState = dispatchAction({
                        config: args.config,
                        runtimeState,
                        cosmosData: args.cosmosData,
                        action: {
                            command: 'select_input_to_bind',
                            roleName: route.roleName,
                        },
                    });

                    if (nextBuilderRuntimeState !== runtimeState) {
                        runtimeCallbacks?.onRuntimeStateChange?.(nextBuilderRuntimeState);
                    }

                    args.baseItemBehavior?.onIntent?.(intent);
                    return;
                }
                case 'tool-step-selection': {
                    const nextBuilderRuntimeState = dispatchAction({
                        config: args.config,
                        runtimeState,
                        cosmosData: args.cosmosData,
                        action: {
                            command: 'select_tool_step_to_bind',
                            stepKey: route.stepKey,
                        },
                    });

                    if (nextBuilderRuntimeState !== runtimeState) {
                        runtimeCallbacks?.onRuntimeStateChange?.(nextBuilderRuntimeState);
                    }

                    args.baseItemBehavior?.onIntent?.(intent);
                    return;
                }
                case 'bind-internal': {
                    const nextBuilderRuntimeState = dispatchAction({
                        config: args.config,
                        runtimeState,
                        cosmosData: args.cosmosData,
                        action: {
                            command: 'bind_internal_input_potential',
                            sourceAddress: route.sourceAddress,
                        },
                    });

                    if (nextBuilderRuntimeState !== runtimeState) {
                        runtimeCallbacks?.onRuntimeStateChange?.(nextBuilderRuntimeState);
                    }

                    args.baseItemBehavior?.onIntent?.(intent);
                    return;
                }
                case 'ignore':
                default: {
                    args.baseItemBehavior?.onIntent?.(intent);
                    return;
                }
            }
        },
    };
}

